import { flow, toPairs, groupBy, map, sortBy, filter } from 'lodash/fp';
import hasItemMeasure from './utils/hasItemMeasure';
import groupByMeasure from './utils/groupByMeasure';
import { groupByMeasureCoverPanels }  from './utils/groupByMeasure';
import filterValidDesignText from './utils/filterValidDesignText';
import findImage from './utils/findImage';
import makeDisplayName from './utils/makeDisplayName';
import makeId from './utils/makeId';
import makeDisplayProductName from './utils/makeDisplayProductName';

function createCoverPanels(material, items, thickness2id, sinkType, measurements, country, language) {
  const productTypeName = 'cover panel';
  const itemsWithCoverPanels = items.filter((item) =>
    item.ProductTypeNameGlobal === productTypeName
  );

  return flow(
    filterValidDesignText('expression', material.id),
    groupBy((item) => makeId(item, material.includeProductNameInExpression, country, language, "coverpanel")),
    toPairs,
    map(([id, itemsWithSameDesign]) => ({
        id,
        itemNo: itemsWithSameDesign[0].ItemNo,
        displayProductName: makeDisplayProductName(itemsWithSameDesign[0], material.includeProductNameInExpression),
        displayName: makeDisplayName(itemsWithSameDesign[0]),
        displayImage: findImage(itemsWithSameDesign[0], 'S3', { ImageType: 'Quality Product Picture' }, 1),
        displayImageLarge: findImage(itemsWithSameDesign[0], 'S4', { ImageType: 'Quality Product Picture' }, 1),
        cornerImage: findImage(itemsWithSameDesign[0], 'S3', { ImageType: 'Quality Product Picture' }),
        cornerImageLarge: findImage(itemsWithSameDesign[0], 'S4', { ImageType: 'Quality Product Picture' }),
        minPrice: itemsWithSameDesign.reduce((prev, i2) => Math.min(prev, i2.price), itemsWithSameDesign[0].price),
        sinkType: sinkType(itemsWithSameDesign),
        thicknesses: groupByMeasureCoverPanels(itemsWithSameDesign, 'Thickness')
      .map((itemsWithSameThickness) => ({
          id: thickness2id(itemsWithSameThickness.measure),
          itemNo: itemsWithSameThickness.items[0].ItemNo,
          thickness: itemsWithSameThickness.measure,
          cornerImage: findImage(itemsWithSameThickness.items[0], 'S3', { ImageType: 'Quality Product Picture' }),
          cornerImageLarge: findImage(itemsWithSameThickness.items[0], 'S4', { ImageType: 'Quality Product Picture' }),
          functionalImage: findImage(itemsWithSameThickness.items[0], 'S4', { ImageType: 'Functional Product Picture' }),
          priceByArea: hasItemMeasure(itemsWithSameThickness.items[0], 'Area'),
          depths: groupByMeasureCoverPanels(itemsWithSameThickness.items, 'Width')
        .map((itemsWithSameDepth) =>  {
            return {
              minLength: groupByMeasure(itemsWithSameDepth.items, 'Min. length')[0].measure,
              minDepth: groupByMeasure(itemsWithSameDepth.items, 'Min. depth')[0].measure,
              maxWidth: groupByMeasure(itemsWithSameDepth.items, 'Width'),
              length: groupByMeasure(itemsWithSameDepth.items, 'Height'),
              item: itemsWithSameDepth.items[0],
            }}),
      })).filter((thicknessWithId) => {
        if (!thicknessWithId.id) {
          // eslint-disable-next-line no-console
          console.error('Excluding thickness:', thicknessWithId.thickness, 'not found in business rules for items', thicknessWithId.depths.map((depth) => depth.item.ItemNo));
          return false;
        }
        return true;
      }),
    })),
    filter((expr) => {
      if (expr.thicknesses.length === 0) {
        // eslint-disable-next-line no-console
        console.error('Excluding expression:', expr.id, 'due to no available thicknesses');
        return false;
      }
      return true;
    }),
    sortBy(['minPrice', 'displayName'])
  )(itemsWithCoverPanels);
}

export function generateCoverPanels(materialId, businessRules, items, thicknesses = [], sinkType, country, language) {
  const { materials, measurements, useCoverPanels } = businessRules;
  if(!useCoverPanels){
    return null;
  }
  const thickness2id = (thickness) => {
    const result = thicknesses.find((t) => thickness >= t.min && thickness <= t.max);
    return result && result.id;
  };

  const foundMaterial = materials.find((material) => material.id === materialId);

  if (foundMaterial) {
    return createCoverPanels(foundMaterial, items, thickness2id, sinkType, measurements, country, language);
  }

  return [];
}
